import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useVendors, useNextPONumber, useCreatePurchaseOrder, useUpdatePurchaseOrder } from '@/hooks/usePurchaseOrder';
import type { CreatePORequest, Vendor, POResponse } from '@/types/purchaseorder';
import {
  DEFAULT_CURRENCY,
  DEFAULT_TAX_RATE,
  PAYMENT_TERMS_OPTIONS,
  CURRENCY_OPTIONS,
} from '@/types/purchaseorder';
import { calculateTotals, formatCurrency } from '@/services/poService';
import { PartCodeRecord } from '@/services/bomService';
import PartSelectionDialog, { QuotationPricing } from './PartSelectionDialog';

interface PurchaseOrderDialogProps {
  editingPO?: POResponse | null;
  onClose: () => void;
  onSuccess?: () => void;
}

interface LineItemInput {
  product_name: string;
  specification: string;
  quantity: number;
  unit_price: number;
  currency: string;
  remarks: string;
}

const emptyLineItem: LineItemInput = {
  product_name: '',
  specification: '',
  quantity: 0,
  unit_price: 0,
  currency: DEFAULT_CURRENCY,
  remarks: '',
};

const PurchaseOrderDialog: React.FC<PurchaseOrderDialogProps> = ({ editingPO, onClose, onSuccess }) => {
  const { data: vendors, isLoading: vendorsLoading } = useVendors();
  const { data: nextPONumber } = useNextPONumber();
  const createPOMutation = useCreatePurchaseOrder();
  const updatePOMutation = useUpdatePurchaseOrder();

  const isEditMode = !!editingPO;

  // Form state
  const [vendorCode, setVendorCode] = useState('');
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [purchaseDate, setPurchaseDate] = useState(new Date().toISOString().split('T')[0]);
  const [expectedDelivery, setExpectedDelivery] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [notes, setNotes] = useState('');
  const [lineItems, setLineItems] = useState<LineItemInput[]>([{ ...emptyLineItem }]);

  // Part selection dialog state
  const [showPartSelector, setShowPartSelector] = useState(false);
  const [editingLineIndex, setEditingLineIndex] = useState<number | null>(null);

  // Pre-populate form when editing
  useEffect(() => {
    if (editingPO) {
      setVendorCode(editingPO.vendor_code);
      setPurchaseDate(editingPO.purchase_date.split('T')[0]);
      setExpectedDelivery(editingPO.expected_delivery.split('T')[0]);
      setDeliveryAddress(editingPO.delivery_address);
      setPaymentTerms(editingPO.payment_terms || '');
      setCurrency(editingPO.currency);
      setNotes(editingPO.notes || '');
      setLineItems(
        editingPO.items.map((item) => ({
          product_name: item.product_name,
          specification: item.specification,
          quantity: item.quantity,
          unit_price: item.unit_price,
          currency: item.currency,
          remarks: item.remarks || '',
        }))
      );
    }
  }, [editingPO]);

  // Calculate totals
  const { subtotal, taxAmount, grandTotal } = calculateTotals(
    lineItems.map((item) => ({ quantity: item.quantity, unit_price: item.unit_price })),
    DEFAULT_TAX_RATE
  );

  // Update vendor info when vendor code changes
  useEffect(() => {
    if (vendorCode && vendors) {
      const vendor = vendors.find((v) => v.code === vendorCode);
      setSelectedVendor(vendor || null);
    } else {
      setSelectedVendor(null);
    }
  }, [vendorCode, vendors]);

  // Update line item currency when main currency changes
  useEffect(() => {
    setLineItems((items) =>
      items.map((item) => ({ ...item, currency }))
    );
  }, [currency]);

  const handleAddLineItem = () => {
    setLineItems([...lineItems, { ...emptyLineItem, currency }]);
  };

  const handleRemoveLineItem = (index: number) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const handleLineItemChange = (index: number, field: keyof LineItemInput, value: string | number) => {
    setLineItems((items) =>
      items.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const handleOpenPartSelector = (index: number) => {
    setEditingLineIndex(index);
    setShowPartSelector(true);
  };

  const handlePartSelected = (part: PartCodeRecord, pricing?: QuotationPricing) => {
    if (editingLineIndex === null) return;

    setLineItems((items) =>
      items.map((item, i) =>
        i === editingLineIndex
          ? {
              ...item,
              product_name: part.vendor_product || part.part_number || '',
              specification: part.vendor_spec || '',
              // Keep existing quantity/price if already set, or use quotation price
              quantity: item.quantity || 1,
              unit_price: pricing?.unitPrice ?? item.unit_price ?? 0,
              remarks: part.brand ? `${part.brand} | ${part.part_number}` : part.part_number || '',
            }
          : item
      )
    );
    setEditingLineIndex(null);
  };

  const handleSubmit = async () => {
    // Validation
    if (!vendorCode) {
      toast.error('Please select a vendor');
      return;
    }
    if (!purchaseDate) {
      toast.error('Please enter purchase date');
      return;
    }
    if (!expectedDelivery) {
      toast.error('Please enter expected delivery date');
      return;
    }
    if (!deliveryAddress) {
      toast.error('Please enter delivery address');
      return;
    }

    const validItems = lineItems.filter(
      (item) => item.product_name && item.quantity > 0 && item.unit_price > 0
    );
    if (validItems.length === 0) {
      toast.error('Please add at least one valid line item');
      return;
    }

    const request: CreatePORequest = {
      vendor_code: vendorCode,
      purchase_date: purchaseDate,
      expected_delivery: expectedDelivery,
      delivery_address: deliveryAddress,
      payment_terms: paymentTerms,
      currency,
      notes,
      items: validItems,
    };

    try {
      if (isEditMode && editingPO) {
        // Update existing PO
        await updatePOMutation.mutateAsync({ id: editingPO.po_id, request });
        toast.success(`採購單 ${editingPO.po_number} 已更新`);
      } else {
        // Create new PO
        const result = await createPOMutation.mutateAsync(request);
        toast.success(`採購單 ${result.po_number} 已建立`);
        if (result.sheet_url) {
          toast.success(
            <span>
              表單已產生！{' '}
              <a href={result.sheet_url} target="_blank" rel="noopener noreferrer" className="underline">
                開啟
              </a>
            </span>,
            { duration: 5000 }
          );
        }
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(`${isEditMode ? '更新' : '建立'}失敗: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-t-xl sm:rounded-lg shadow-xl w-full sm:max-w-5xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-bold text-white truncate">
                {isEditMode ? '編輯採購單' : '新增採購單'}
              </h2>
              <p className="text-blue-100 text-xs sm:text-sm truncate">
                {isEditMode
                  ? `採購單號: ${editingPO?.po_number}`
                  : nextPONumber
                  ? `採購單號: ${nextPONumber}`
                  : '載入中...'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1">
          {/* Vendor Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 border-b pb-2">
              廠商資料
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  廠商代號 <span className="text-red-500">*</span>
                </label>
                <select
                  value={vendorCode}
                  onChange={(e) => setVendorCode(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-900"
                  disabled={vendorsLoading}
                >
                  <option value="">Select vendor...</option>
                  {vendors?.map((vendor) => (
                    <option key={vendor.code} value={vendor.code}>
                      {vendor.code} - {vendor.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  廠商名稱
                </label>
                <input
                  type="text"
                  value={selectedVendor?.name || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-200 dark:border-gray-300 dark:text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  統一編號
                </label>
                <input
                  type="text"
                  value={selectedVendor?.tax_id || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-200 dark:border-gray-300 dark:text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  聯絡人
                </label>
                <input
                  type="text"
                  value={selectedVendor?.contact || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-200 dark:border-gray-300 dark:text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  電話
                </label>
                <input
                  type="text"
                  value={selectedVendor?.phone || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-200 dark:border-gray-300 dark:text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  E-mail
                </label>
                <input
                  type="text"
                  value={selectedVendor?.email || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-200 dark:border-gray-300 dark:text-gray-900"
                />
              </div>
            </div>
          </div>

          {/* Order Info Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 border-b pb-2">
              訂單資料
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  採購日期 <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={purchaseDate}
                  onChange={(e) => setPurchaseDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  期望交貨日 <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={expectedDelivery}
                  onChange={(e) => setExpectedDelivery(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  付款條件
                </label>
                <select
                  value={paymentTerms}
                  onChange={(e) => setPaymentTerms(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-900"
                >
                  <option value="">Select...</option>
                  {PAYMENT_TERMS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  送貨地址 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  placeholder="Enter delivery address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  幣別
                </label>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-900"
                >
                  {CURRENCY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Line Items Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4 border-b pb-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">採購項目</h3>
              <button
                type="button"
                onClick={handleAddLineItem}
                className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded-lg flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Item
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                    <th className="px-2 py-2 text-left w-10">項次</th>
                    <th className="px-2 py-2 text-left">品名</th>
                    <th className="px-2 py-2 text-left">規格</th>
                    <th className="px-2 py-2 text-right w-24">數量</th>
                    <th className="px-2 py-2 text-right w-28">單價</th>
                    <th className="px-2 py-2 text-right w-28">金額</th>
                    <th className="px-2 py-2 text-left">備註</th>
                    <th className="px-2 py-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((item, index) => (
                    <tr key={index} className="border-b dark:border-gray-600 text-gray-900 dark:text-gray-200">
                      <td className="px-2 py-2 text-center">{index + 1}</td>
                      <td className="px-2 py-2">
                        <div className="flex gap-1">
                          <input
                            type="text"
                            value={item.product_name}
                            onChange={(e) => handleLineItemChange(index, 'product_name', e.target.value)}
                            onClick={() => handleOpenPartSelector(index)}
                            placeholder="點擊選擇零件..."
                            className="flex-1 px-2 py-1 border border-gray-300 rounded cursor-pointer hover:border-green-500 focus:ring-2 focus:ring-green-500 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-900"
                            readOnly
                          />
                          <button
                            type="button"
                            onClick={() => handleOpenPartSelector(index)}
                            className="px-2 py-1 text-green-600 hover:bg-green-50 rounded transition-colors"
                            title="選擇零件"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </button>
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="text"
                          value={item.specification}
                          onChange={(e) => handleLineItemChange(index, 'specification', e.target.value)}
                          placeholder="Specification"
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-900"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="number"
                          value={item.quantity || ''}
                          onChange={(e) => handleLineItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                          min="0"
                          className="w-full px-2 py-1 border border-gray-300 rounded text-right focus:ring-2 focus:ring-blue-500 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-900"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="number"
                          value={item.unit_price || ''}
                          onChange={(e) =>
                            handleLineItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)
                          }
                          min="0"
                          step="0.01"
                          className="w-full px-2 py-1 border border-gray-300 rounded text-right focus:ring-2 focus:ring-blue-500 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-900"
                        />
                      </td>
                      <td className="px-2 py-2 text-right">
                        {formatCurrency(item.quantity * item.unit_price, currency)}
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="text"
                          value={item.remarks}
                          onChange={(e) => handleLineItemChange(index, 'remarks', e.target.value)}
                          placeholder="Remarks"
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-900"
                        />
                      </td>
                      <td className="px-2 py-2">
                        {lineItems.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveLineItem(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 border-b pb-2">
              金額總計
            </h3>
            <div className="flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">總金額:</span>
                  <span className="font-medium text-gray-900 dark:text-gray-200">{formatCurrency(subtotal, currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">稅額 (5%):</span>
                  <span className="font-medium text-gray-900 dark:text-gray-200">{formatCurrency(taxAmount, currency)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t pt-2 text-gray-900 dark:text-white">
                  <span>總計:</span>
                  <span className="text-blue-600 dark:text-blue-400">{formatCurrency(grandTotal, currency)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Notes Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              備註
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Additional notes..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-900"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 dark:bg-gray-700 px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 flex-shrink-0 pb-safe">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-4 py-3 sm:py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px]"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={createPOMutation.isLoading || updatePOMutation.isLoading}
            className="w-full sm:w-auto px-4 py-3 sm:py-2 text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-h-[44px]"
          >
            {(createPOMutation.isLoading || updatePOMutation.isLoading) && (
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {isEditMode ? '更新採購單' : '建立採購單'}
          </button>
        </div>
      </div>

      {/* Part Selection Dialog */}
      {showPartSelector && (
        <PartSelectionDialog
          vendorCode={vendorCode || undefined}
          onClose={() => {
            setShowPartSelector(false);
            setEditingLineIndex(null);
          }}
          onSelect={handlePartSelected}
        />
      )}
    </div>
  );
};

export default PurchaseOrderDialog;
